/**
 * Easel Portfolio API Client
 *
 * API client functions for the Easel Portfolio management system.
 * All endpoints are prefixed with /api/v1/easel
 */

import { apiClient } from '@/lib/api/client'
import type { Artwork } from '@/types/entities'
import type {
  Portfolio,
  PortfolioStatus,
  Profile,
  ProfileLink,
  SocialAccounts,
  StyleConfig,
  StyleRecommendations,
  AppearanceState,
  SaveAppearanceRequest,
  PaletteSuggestionRequest,
  PaletteSuggestionResult,
  Collection,
  CollectionWithArtworks,
  CreateCollectionData,
  UpdateCollectionData,
  JournalCategory,
  JournalPost,
  JournalPostWithContent,
  CreateJournalCategoryData,
  UpdateJournalCategoryData,
  CreateJournalPostData,
  UpdateJournalPostData,
  ShopCategory,
  CreateShopCategoryData,
  UpdateShopCategoryData,
  ShortLink,
  CreateShortLinkData,
  ContactInquiry,
  InquiryStatus,
  SiteConfig,
  UpdateSiteConfigData,
  Commission,
  CommissionDetail,
  CommissionMessage,
  CommissionStatus,
  CommissionSource,
  CommissionInvoiceResult,
  InvoiceKind,
  Reconciliation,
  UpdateCommissionData,
  EaselApiResponse,
  EaselPaginatedResponse,
  EaselMediaKind,
  EaselMediaResult,
} from '../types'

// Base path for Easel API endpoints
const EASEL_BASE = '/api/v1/easel'

// =============================================================================
// Portfolio Management
// =============================================================================

export const portfolioApi = {
  /**
   * Get portfolio status for the current user
   */
  getStatus: async (): Promise<PortfolioStatus> => {
    const response = await apiClient.get<EaselApiResponse<PortfolioStatus>>(
      `${EASEL_BASE}/portfolio/status`
    )
    return response.data.data
  },

  /**
   * Initialize a new portfolio
   */
  initialize: async (data: {
    handle?: string
    name?: string
    bio?: string
    location?: string
  }): Promise<Portfolio> => {
    const response = await apiClient.post<EaselApiResponse<Portfolio>>(
      `${EASEL_BASE}/portfolio/init`,
      data
    )
    return response.data.data
  },

  /**
   * Link an existing portfolio (for migration)
   */
  link: async (portfolioUid: string): Promise<Portfolio> => {
    const response = await apiClient.post<EaselApiResponse<Portfolio>>(
      `${EASEL_BASE}/portfolio/link`,
      { portfolioUid }
    )
    return response.data.data
  },
}

// =============================================================================
// Profile Management
// =============================================================================

export const profileApi = {
  /**
   * Get current user's profile
   */
  getProfile: async (): Promise<Profile> => {
    const response = await apiClient.get<EaselApiResponse<Profile>>(
      `${EASEL_BASE}/profile`
    )
    return response.data.data
  },

  /**
   * Update profile
   */
  updateProfile: async (data: {
    handle?: string
    name?: string
    bio?: string
    location?: string
    template?: string
  }): Promise<Profile> => {
    const response = await apiClient.patch<EaselApiResponse<Profile>>(
      `${EASEL_BASE}/profile`,
      data
    )
    return response.data.data
  },

  /**
   * Get profile links
   */
  getLinks: async (): Promise<ProfileLink[]> => {
    const response = await apiClient.get<EaselApiResponse<ProfileLink[]>>(
      `${EASEL_BASE}/links`
    )
    return response.data.data
  },

  /**
   * Create a new link
   */
  createLink: async (data: Omit<ProfileLink, 'id' | 'position'>): Promise<ProfileLink> => {
    const response = await apiClient.post<EaselApiResponse<ProfileLink>>(
      `${EASEL_BASE}/links`,
      data
    )
    return response.data.data
  },

  /**
   * Update a link
   */
  updateLink: async (linkId: number, data: Partial<ProfileLink>): Promise<ProfileLink> => {
    const response = await apiClient.patch<EaselApiResponse<ProfileLink>>(
      `${EASEL_BASE}/links/${linkId}`,
      data
    )
    return response.data.data
  },

  /**
   * Delete a link
   */
  deleteLink: async (linkId: number): Promise<void> => {
    await apiClient.delete(`${EASEL_BASE}/links/${linkId}`)
  },

  /**
   * Reorder links
   */
  reorderLinks: async (linkIds: number[]): Promise<ProfileLink[]> => {
    const response = await apiClient.post<EaselApiResponse<ProfileLink[]>>(
      `${EASEL_BASE}/links/reorder`,
      { linkIds }
    )
    return response.data.data
  },

  /**
   * Get social accounts
   */
  getSocial: async (): Promise<SocialAccounts> => {
    const response = await apiClient.get<EaselApiResponse<SocialAccounts>>(
      `${EASEL_BASE}/social`
    )
    return response.data.data
  },

  /**
   * Update social accounts
   */
  updateSocial: async (data: Partial<SocialAccounts>): Promise<SocialAccounts> => {
    const response = await apiClient.patch<EaselApiResponse<SocialAccounts>>(
      `${EASEL_BASE}/social`,
      data
    )
    return response.data.data
  },

  /**
   * Get style configuration
   */
  getStyle: async (): Promise<StyleConfig> => {
    const response = await apiClient.get<EaselApiResponse<StyleConfig>>(
      `${EASEL_BASE}/style`
    )
    return response.data.data
  },

  /**
   * Update style configuration
   */
  updateStyle: async (data: Partial<StyleConfig>): Promise<StyleConfig> => {
    const response = await apiClient.patch<EaselApiResponse<StyleConfig>>(
      `${EASEL_BASE}/style`,
      data
    )
    return response.data.data
  },

  /** Palettes to recommend: the artist's own (from their genome), or null without one. */
  getStyleRecommendations: async (): Promise<StyleRecommendations> => {
    const response = await apiClient.get<EaselApiResponse<StyleRecommendations>>(
      `${EASEL_BASE}/style/recommendations`
    )
    return response.data.data
  },

  /** The artist's surfaces (link-in-bio, website), what each allows, and their colours now. */
  getAppearance: async (): Promise<AppearanceState> => {
    const response = await apiClient.get<EaselApiResponse<AppearanceState>>(`${EASEL_BASE}/appearance`)
    return response.data.data
  },

  /** Save one palette to one or both surfaces. */
  saveAppearance: async (data: SaveAppearanceRequest): Promise<AppearanceState & { saved: string[] }> => {
    const response = await apiClient.put<EaselApiResponse<AppearanceState & { saved: string[] }>>(
      `${EASEL_BASE}/appearance`,
      data
    )
    return response.data.data
  },

  /** Palette ideas from a description and/or an image. */
  suggestPalettes: async (input: PaletteSuggestionRequest): Promise<PaletteSuggestionResult> => {
    const form = new FormData()
    if (input.prompt) form.append('prompt', input.prompt)
    if (input.current) form.append('current', JSON.stringify(input.current))
    if (input.image) form.append('file', input.image)
    const response = await apiClient.post<EaselApiResponse<PaletteSuggestionResult>>(
      `${EASEL_BASE}/style/suggest`,
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    )
    return response.data.data
  },

  /**
   * Get the artist's link-in-bio URL.
   *
   * `url` carries a cache-busting `ts` and is what the preview iframe loads;
   * `publicUrl` is the clean one to show or link to. Both come from the API so
   * the host lives in one place (PROFILE_BASE_URL) rather than being hardcoded
   * per-screen — which is how the handle field ended up advertising a different,
   * non-working host than the preview pane.
   *
   * `ownDomain` is true when the artist's link page lives on their own domain
   * (`links.<artist>.art`); `sharedUrl` is always the `arthaus.bio/<handle>` address.
   */
  getPreviewUrl: async (): Promise<BioLink> => {
    const response = await apiClient.get<EaselApiResponse<BioLink>>(`${EASEL_BASE}/preview`)
    return response.data.data
  },
}

export interface BioLink {
  url: string
  publicUrl: string
  sharedUrl?: string
  ownDomain?: boolean
  handle: string
}

// =============================================================================
// Collections Management
// =============================================================================

export const collectionsApi = {
  /**
   * Get all collections
   */
  getCollections: async (params?: {
    featured?: boolean
    visible?: boolean
    limit?: number
    offset?: number
  }): Promise<{ data: Collection[]; total: number }> => {
    const response = await apiClient.get<EaselPaginatedResponse<Collection>>(
      `${EASEL_BASE}/collections`,
      { params }
    )
    return {
      data: response.data.data,
      total: response.data.pagination.total,
    }
  },

  /**
   * Get a single collection with artworks
   */
  getCollection: async (id: number): Promise<CollectionWithArtworks> => {
    const response = await apiClient.get<EaselApiResponse<CollectionWithArtworks>>(
      `${EASEL_BASE}/collections/${id}`
    )
    return response.data.data
  },

  /**
   * Create a new collection
   */
  createCollection: async (data: CreateCollectionData): Promise<Collection> => {
    const response = await apiClient.post<EaselApiResponse<Collection>>(
      `${EASEL_BASE}/collections`,
      data
    )
    return response.data.data
  },

  /**
   * Update a collection
   */
  updateCollection: async (id: number, data: UpdateCollectionData): Promise<Collection> => {
    const response = await apiClient.patch<EaselApiResponse<Collection>>(
      `${EASEL_BASE}/collections/${id}`,
      data
    )
    return response.data.data
  },

  /**
   * Delete a collection
   */
  deleteCollection: async (id: number): Promise<void> => {
    await apiClient.delete(`${EASEL_BASE}/collections/${id}`)
  },

  /**
   * Add artwork to collection
   */
  addArtwork: async (
    collectionId: number,
    artworkId: number,
    displayOrder?: number
  ): Promise<void> => {
    await apiClient.post(`${EASEL_BASE}/collections/${collectionId}/artworks`, {
      artworkId,
      displayOrder,
    })
  },

  /**
   * Remove artwork from collection
   */
  removeArtwork: async (collectionId: number, artworkId: number): Promise<void> => {
    await apiClient.delete(`${EASEL_BASE}/collections/${collectionId}/artworks/${artworkId}`)
  },

  /**
   * Reorder artworks in collection
   */
  reorderArtworks: async (collectionId: number, artworkIds: number[]): Promise<void> => {
    await apiClient.post(`${EASEL_BASE}/collections/${collectionId}/artworks/reorder`, {
      artworkIds,
    })
  },
}

// =============================================================================
// Artworks (source for the collection picker)
// =============================================================================

export const artworksApi = {
  /**
   * List the signed-in artist's artworks.
   *
   * Uses the core AMS artwork list route (JWT-scoped to req.user.id), which is
   * the same source the artworks feature uses. `status` is omitted so all of
   * the artist's artworks are returned for selection into a collection.
   */
  listMine: async (limit = 200): Promise<Artwork[]> => {
    const response = await apiClient.get<Artwork[]>('/api/artworks', {
      params: { start: 0, limit },
    })
    return response.data || []
  },
}

// =============================================================================
// Media Upload (source for the EaselImageField "Upload" tab)
// =============================================================================

export const mediaApi = {
  /**
   * Upload an image file to GCS via POST /easel/media. Returns the hosted
   * original + preview URLs (plus dimensions/bytes). `kind` namespaces the
   * object path (journal/collection/site/profile).
   *
   * The Content-Type is set to multipart/form-data so axios overrides the
   * client's JSON default and re-computes the boundary for the FormData body.
   */
  upload: async (
    formFile: File,
    kind: EaselMediaKind = 'journal'
  ): Promise<EaselMediaResult> => {
    const form = new FormData()
    form.append('file', formFile)
    form.append('kind', kind)
    const response = await apiClient.post<EaselApiResponse<EaselMediaResult>>(
      `${EASEL_BASE}/media`,
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    )
    return response.data.data
  },
}

// =============================================================================
// Journal Management
// =============================================================================

export const journalApi = {
  /**
   * Get all journal categories
   */
  getCategories: async (): Promise<JournalCategory[]> => {
    const response = await apiClient.get<EaselApiResponse<JournalCategory[]>>(
      `${EASEL_BASE}/journal/categories`
    )
    return response.data.data
  },

  /**
   * Create a journal category
   */
  createCategory: async (data: CreateJournalCategoryData): Promise<JournalCategory> => {
    const response = await apiClient.post<EaselApiResponse<JournalCategory>>(
      `${EASEL_BASE}/journal/categories`,
      data
    )
    return response.data.data
  },

  /**
   * Update a journal category
   */
  updateCategory: async (
    id: number,
    data: UpdateJournalCategoryData
  ): Promise<JournalCategory> => {
    const response = await apiClient.patch<EaselApiResponse<JournalCategory>>(
      `${EASEL_BASE}/journal/categories/${id}`,
      data
    )
    return response.data.data
  },

  /**
   * Delete a journal category
   */
  deleteCategory: async (id: number): Promise<void> => {
    await apiClient.delete(`${EASEL_BASE}/journal/categories/${id}`)
  },

  /**
   * Get all journal posts
   */
  getPosts: async (params?: {
    categoryId?: number
    featured?: boolean
    published?: boolean
    limit?: number
    offset?: number
  }): Promise<{ data: JournalPost[]; total: number }> => {
    const response = await apiClient.get<EaselPaginatedResponse<JournalPost>>(
      `${EASEL_BASE}/journal/posts`,
      { params }
    )
    return {
      data: response.data.data,
      total: response.data.pagination.total,
    }
  },

  /**
   * Get a single journal post with content
   */
  getPost: async (id: number): Promise<JournalPostWithContent> => {
    const response = await apiClient.get<EaselApiResponse<JournalPostWithContent>>(
      `${EASEL_BASE}/journal/posts/${id}`
    )
    return response.data.data
  },

  /**
   * Create a journal post
   */
  createPost: async (data: CreateJournalPostData): Promise<JournalPost> => {
    const response = await apiClient.post<EaselApiResponse<JournalPost>>(
      `${EASEL_BASE}/journal/posts`,
      data
    )
    return response.data.data
  },

  /**
   * Update a journal post
   */
  updatePost: async (id: number, data: UpdateJournalPostData): Promise<JournalPost> => {
    const response = await apiClient.patch<EaselApiResponse<JournalPost>>(
      `${EASEL_BASE}/journal/posts/${id}`,
      data
    )
    return response.data.data
  },

  /**
   * Delete a journal post
   */
  deletePost: async (id: number): Promise<void> => {
    await apiClient.delete(`${EASEL_BASE}/journal/posts/${id}`)
  },
}

// =============================================================================
// Shop Management
// =============================================================================

export const shopApi = {
  /**
   * Get all shop categories
   */
  getCategories: async (params?: {
    enabled?: boolean
    featured?: boolean
  }): Promise<ShopCategory[]> => {
    const response = await apiClient.get<EaselApiResponse<ShopCategory[]>>(
      `${EASEL_BASE}/shop/categories`,
      { params }
    )
    return response.data.data
  },

  /**
   * Get a single shop category
   */
  getCategory: async (id: number): Promise<ShopCategory> => {
    const response = await apiClient.get<EaselApiResponse<ShopCategory>>(
      `${EASEL_BASE}/shop/categories/${id}`
    )
    return response.data.data
  },

  /**
   * Create a shop category
   */
  createCategory: async (data: CreateShopCategoryData): Promise<ShopCategory> => {
    const response = await apiClient.post<EaselApiResponse<ShopCategory>>(
      `${EASEL_BASE}/shop/categories`,
      data
    )
    return response.data.data
  },

  /**
   * Update a shop category
   */
  updateCategory: async (id: number, data: UpdateShopCategoryData): Promise<ShopCategory> => {
    const response = await apiClient.patch<EaselApiResponse<ShopCategory>>(
      `${EASEL_BASE}/shop/categories/${id}`,
      data
    )
    return response.data.data
  },

  /**
   * Delete a shop category
   */
  deleteCategory: async (id: number): Promise<void> => {
    await apiClient.delete(`${EASEL_BASE}/shop/categories/${id}`)
  },
}

// =============================================================================
// Contact Management
// =============================================================================

export const contactApi = {
  /**
   * Get all contact inquiries
   */
  getInquiries: async (params?: {
    status?: InquiryStatus
    inquiryType?: string
    limit?: number
    offset?: number
  }): Promise<{ data: ContactInquiry[]; total: number }> => {
    const response = await apiClient.get<EaselPaginatedResponse<ContactInquiry>>(
      `${EASEL_BASE}/contact/inquiries`,
      { params }
    )
    return {
      data: response.data.data,
      total: response.data.pagination.total,
    }
  },

  /**
   * Get a single contact inquiry
   */
  getInquiry: async (id: number): Promise<ContactInquiry> => {
    const response = await apiClient.get<EaselApiResponse<ContactInquiry>>(
      `${EASEL_BASE}/contact/inquiries/${id}`
    )
    return response.data.data
  },

  /**
   * Update inquiry status
   */
  updateStatus: async (id: number, status: InquiryStatus): Promise<ContactInquiry> => {
    const response = await apiClient.patch<EaselApiResponse<ContactInquiry>>(
      `${EASEL_BASE}/contact/inquiries/${id}/status`,
      { status }
    )
    return response.data.data
  },
}

// =============================================================================
// Commissions Management
// =============================================================================

export const commissionsApi = {
  /**
   * Whether the commissions feature is enabled for the current artist
   * (release gate). Used to hide the feature until rollout.
   */
  getAccess: async (): Promise<{ enabled: boolean }> => {
    const response = await apiClient.get<EaselApiResponse<{ enabled: boolean }>>(
      `${EASEL_BASE}/commissions/access`
    )
    return response.data.data
  },

  /**
   * List commissions (pipeline) for the current artist
   */
  getCommissions: async (params?: {
    status?: CommissionStatus
    source?: CommissionSource
    limit?: number
    offset?: number
  }): Promise<{ data: Commission[]; total: number }> => {
    const response = await apiClient.get<EaselPaginatedResponse<Commission>>(
      `${EASEL_BASE}/commissions`,
      { params }
    )
    return {
      data: response.data.data,
      total: response.data.pagination.total,
    }
  },

  /**
   * Get a single commission with thread, events, payments, and split
   */
  getCommission: async (id: number): Promise<CommissionDetail> => {
    const response = await apiClient.get<EaselApiResponse<CommissionDetail>>(
      `${EASEL_BASE}/commissions/${id}`
    )
    return response.data.data
  },

  /**
   * Promote a contact inquiry into a commission
   */
  promoteInquiry: async (
    inquiryId: number,
    source?: CommissionSource
  ): Promise<Commission> => {
    const response = await apiClient.post<EaselApiResponse<Commission>>(
      `${EASEL_BASE}/commissions`,
      { inquiryId, source }
    )
    return response.data.data
  },

  /**
   * Update a commission — set quote/deposit and/or transition status
   */
  updateCommission: async (
    id: number,
    data: UpdateCommissionData
  ): Promise<CommissionDetail> => {
    const response = await apiClient.patch<EaselApiResponse<CommissionDetail>>(
      `${EASEL_BASE}/commissions/${id}`,
      data
    )
    return response.data.data
  },

  /**
   * Post an artist reply into the commission thread
   */
  createMessage: async (id: number, bodyText: string): Promise<CommissionMessage> => {
    const response = await apiClient.post<EaselApiResponse<CommissionMessage>>(
      `${EASEL_BASE}/commissions/${id}/messages`,
      { bodyText }
    )
    return response.data.data
  },

  /**
   * Refund a commission (transition to refunded; opt-in Shopify money-back).
   */
  refundCommission: async (id: number, reason?: string): Promise<CommissionDetail> => {
    const response = await apiClient.post<EaselApiResponse<CommissionDetail>>(
      `${EASEL_BASE}/commissions/${id}/refund`,
      { reason }
    )
    return response.data.data
  },

  /**
   * Financial reconciliation report for the artist's commissions.
   */
  getReconciliation: async (): Promise<Reconciliation> => {
    const response = await apiClient.get<EaselApiResponse<Reconciliation>>(
      `${EASEL_BASE}/commissions/reconciliation`
    )
    return response.data.data
  },

  /**
   * Reconciliation as a CSV blob (auth is injected by the apiClient interceptor).
   */
  downloadReconciliationCsv: async (): Promise<Blob> => {
    const response = await apiClient.get(`${EASEL_BASE}/commissions/reconciliation`, {
      params: { format: 'csv' },
      responseType: 'blob',
    })
    return response.data as Blob
  },

  /**
   * Approve a Picasso draft — it becomes a sent message the patron can see.
   */
  approveDraft: async (commissionId: number, messageId: number): Promise<CommissionMessage> => {
    const response = await apiClient.post<EaselApiResponse<CommissionMessage>>(
      `${EASEL_BASE}/commissions/${commissionId}/messages/${messageId}/approve`
    )
    return response.data.data
  },

  /**
   * Dismiss a Picasso draft — never shown to the patron.
   */
  dismissDraft: async (commissionId: number, messageId: number): Promise<CommissionMessage> => {
    const response = await apiClient.post<EaselApiResponse<CommissionMessage>>(
      `${EASEL_BASE}/commissions/${commissionId}/messages/${messageId}/dismiss`
    )
    return response.data.data
  },

  /**
   * Create a Shopify draft-order invoice (deposit | final) for a commission.
   * Returns the patron checkout URL.
   */
  createInvoice: async (
    id: number,
    kind: InvoiceKind,
    opts?: { shippingTitle?: string; shippingAmount?: number }
  ): Promise<CommissionInvoiceResult> => {
    const response = await apiClient.post<EaselApiResponse<CommissionInvoiceResult>>(
      `${EASEL_BASE}/commissions/${id}/invoices`,
      { kind, ...opts }
    )
    return response.data.data
  },
}

// =============================================================================
// Site Configuration
// =============================================================================

export const siteApi = {
  /**
   * Get site configuration
   */
  getConfig: async (): Promise<SiteConfig> => {
    const response = await apiClient.get<EaselApiResponse<SiteConfig>>(
      `${EASEL_BASE}/site/config`
    )
    return response.data.data
  },

  /**
   * Update site configuration
   */
  updateConfig: async (data: UpdateSiteConfigData): Promise<SiteConfig> => {
    const response = await apiClient.patch<EaselApiResponse<SiteConfig>>(
      `${EASEL_BASE}/site/config`,
      data
    )
    return response.data.data
  },

  /**
   * Initialize site configuration
   */
  initialize: async (data: {
    siteName?: string
    artistName?: string
    email?: string
  }): Promise<SiteConfig> => {
    const response = await apiClient.post<EaselApiResponse<SiteConfig>>(
      `${EASEL_BASE}/site/config/init`,
      data
    )
    return response.data.data
  },
}

// =============================================================================
// Hero imagery + mockups
// =============================================================================

export interface MockupArtwork {
  id: number
  name: string
  cdnId: string
  artUrl: string
  mockupCount: number
  hero: { url: string; concept: string | null; template: string | null } | null
}

export interface MockupConcept {
  id: string
  label: string
  /** Whether this scene survives being emptied into a reusable template. */
  templatable: boolean
  /** Why it does not, when it does not — surfaced so the UI never hides an option silently. */
  notTemplatableReason: string | null
}

export interface MockupTemplate {
  id: string
  conceptId: string
  frameless: boolean
  imageUrl: string
  aspect: number
  region: { top: number; left: number; width: number; height: number }
  skew: number
  createdAt: string | null
}

export interface MockupSlide {
  kind: string
  concept?: string
  url: string
  width?: number
  height?: number
  aspect?: number
}

export const mockupApi = {
  /** Works (+ current hero) and the concept catalog, in one call. */
  /**
   * Which render targets this artist's work belongs to, and which one the Hero
   * Studio should default to. Derived server-side from the artist's own
   * artworks, because site config carries no render target in production.
   */
  getTargets: async () => {
    const response = await apiClient.get<{
      success: boolean
      data: Array<{ target: string; artworkCount: number }>
      defaultTarget: string | null
    }>(`${EASEL_BASE}/mockups/targets`)
    return response.data
  },

  getContext: async (target?: string) => {
    const response = await apiClient.get<
      EaselApiResponse<{ artistId: number; target: string | null; artworks: MockupArtwork[]; concepts: MockupConcept[] }>
    >(`${EASEL_BASE}/mockups/context`, { params: target ? { target } : undefined })
    return response.data.data
  },

  listTemplates: async (): Promise<MockupTemplate[]> => {
    const response = await apiClient.get<EaselApiResponse<MockupTemplate[]>>(
      `${EASEL_BASE}/mockups/templates`
    )
    return response.data.data
  },

  /** Render concepts for one artwork. Candidates only — nothing is saved. */
  generateCandidates: async (data: {
    artworkId: number
    concepts: string[]
    motif?: string
    target?: string
  }) => {
    const response = await apiClient.post<EaselApiResponse<{ artworkId: number; slides: MockupSlide[] }>>(
      `${EASEL_BASE}/mockups/candidates`,
      data
    )
    return response.data.data
  },

  setHero: async (data: {
    artworkId: number
    url: string
    target: string
    concept?: string | null
    template?: string | null
    alt?: string
    width?: number
    height?: number
    aspect?: number
  }) => {
    const response = await apiClient.post<
      EaselApiResponse<{ artworkId: number; target: string; count: number; carriedForward: number; cacheCleared: boolean }>
    >(`${EASEL_BASE}/mockups/hero`, data)
    return response.data.data
  },

  applyTemplate: async (data: { templateId: string; target: string; artworkIds?: number[] }) => {
    const response = await apiClient.post<
      EaselApiResponse<{
        templateId: string
        target: string
        applied: Array<{ id: number; name: string; url: string; carriedForward: number }>
        failed: Array<{ id: number; name: string; error: string }>
        cacheCleared: boolean
      }>
    >(`${EASEL_BASE}/mockups/apply-template`, data)
    return response.data.data
  },
}

// =============================================================================
// Scene directions — the human-in-the-loop half
// =============================================================================

export interface SceneSetting {
  id: string
  name: string
  scene: string
}

export interface SceneDirection {
  id: string
  name: string
  rationale: string
  settings: SceneSetting[]
  /** A real empty template with the artist's own work composited in — what would actually ship. */
  preview: string | null
  previewError?: string | null
}

export interface SceneTemplate {
  id: string
  imageUrl: string
  sceneName: string
  directionId: string | null
  directionName: string | null
  createdAt: string | null
}

export const sceneApi = {
  /** Slow by nature: one image render per direction. Nothing is stored. */
  propose: async (data: { target: string; count?: number; sampleArtworkId?: number }) => {
    const response = await apiClient.post<EaselApiResponse<{ target: string; directions: SceneDirection[] }>>(
      `${EASEL_BASE}/mockups/directions/propose`,
      data
    )
    return response.data.data
  },

  build: async (data: { target: string; direction: SceneDirection }) => {
    const response = await apiClient.post<
      EaselApiResponse<{ target: string; built: Array<{ id: string; setting: string }>; failed: Array<{ id: string; reason: string }> }>
    >(`${EASEL_BASE}/mockups/directions/build`, data)
    return response.data.data
  },

  listScenes: async (target: string): Promise<SceneTemplate[]> => {
    const response = await apiClient.get<EaselApiResponse<SceneTemplate[]>>(
      `${EASEL_BASE}/mockups/scenes`,
      { params: { target } }
    )
    return response.data.data
  },

  apply: async (data: { target: string; artworkIds?: number[] }) => {
    const response = await apiClient.post<
      EaselApiResponse<{
        targetId: string
        templates: string[]
        applied: Array<{ id: number; name: string; kept: number; scenes: number }>
        failed: Array<{ id: number; name: string; error: string }>
        cacheCleared: boolean
      }>
    >(`${EASEL_BASE}/mockups/scenes/apply`, data)
    return response.data.data
  },
}

// =============================================================================
// Short Links (arthaus.link)
// =============================================================================

export const shortLinksApi = {
  /**
   * `edgeConfigured` reports whether the API can reach the edge store at all.
   * Surfaced so the UI can say why a new link is not resolving yet, rather than
   * leaving the artist to wonder whether they did something wrong.
   */
  list: async (params?: { limit?: number; offset?: number }) => {
    const response = await apiClient.get<{
      success: boolean
      data: ShortLink[]
      count: number
      edgeConfigured: boolean
    }>(`${EASEL_BASE}/short-links`, { params })
    return response.data
  },

  create: async (data: CreateShortLinkData) => {
    const response = await apiClient.post<{
      success: boolean
      data: ShortLink
      synced: boolean
      syncError?: string
    }>(`${EASEL_BASE}/short-links`, data)
    return response.data
  },

  /** Retiring is a state change, not a delete — a printed link cannot be un-printed. */
  setActive: async (id: number, active: boolean) => {
    const response = await apiClient.patch<{
      success: boolean
      data: ShortLink
      synced: boolean
      syncError?: string
    }>(`${EASEL_BASE}/short-links/${id}`, { active })
    return response.data
  },

  qrUrl: (id: number, size = 512) =>
    `${EASEL_BASE}/short-links/${id}/qr.png?size=${size}`,
}
